/**
 * components/TxnBadges.js
 * Shared status/due-date badges — identical between Penjualan, Pembelian, and
 * TransactionDetailModal (Phase T1c: extracted when a third near-identical copy of these
 * label maps would otherwise have appeared).
 */
import React from "react";

const STATUS_LABEL = { LUNAS: "Lunas", PIUTANG: "Piutang", UTANG: "Utang" };
// Phase T1g: a small leading icon softens the pill — amber "owed" states share one hourglass,
// settled gets a checkmark. Semantic colours (amber/green, `.status-pill--*` in styles.css)
// are unchanged; this only adds the icon and switches the label to Title Case.
const STATUS_ICON = { LUNAS: "✅", PIUTANG: "⏳", UTANG: "⏳" };
const DUE_LABEL = {
  overdue: (d) => `Lewat ${d} hari`,
  today: () => "Hari ini",
  upcoming: (d) => `${d} hari lagi`,
};

export function StatusPill({ status }) {
  return (
    <span className={`status-pill status-pill--${status.toLowerCase()}`}>
      <span aria-hidden="true">{STATUS_ICON[status]}</span> {STATUS_LABEL[status]}
    </span>
  );
}

/**
 * Lunas/Belum Lunas only — never Piutang/Utang. Piutang & Utang batch 3's Internal tab (one row
 * per Transfer, a whole-business view) needs a "paid status" badge that doesn't imply a
 * direction — a Transfer isn't a debt in two directions, so labeling it Piutang or Utang here
 * would contradict the tab's own point. Reuses `StatusPill`'s own lunas/utang CSS classes
 * (`.status-pill--lunas`/`.status-pill--utang` are already visually identical to piutang's own,
 * see styles.css) rather than inventing a new one — only the label/icon differ.
 */
export function PaidStatusPill({ lunas }) {
  return (
    <span className={`status-pill status-pill--${lunas ? "lunas" : "utang"}`}>
      <span aria-hidden="true">{lunas ? "✅" : "⏳"}</span> {lunas ? "Lunas" : "Belum Lunas"}
    </span>
  );
}

/** Renders null when there's nothing due (Lunas or no dueDate) — callers that want a "—"
 *  fallback in that case check `due.kind === "none"` themselves before rendering this. */
export function DueBadge({ due }) {
  if (due.kind === "none") return null;
  return <span className={`due-pill due-pill--${due.kind}`}>{DUE_LABEL[due.kind](due.days)}</span>;
}
